import { get } from "svelte/store";
import { EventTracking } from "@shared/services/event_tracking/events";
import CartStore from "@retailer/svelte/stores/CartStore";

import {
  CartApiMetadataEntity,
  CartProductEntity,
  CartWidgetTargetObjectBase,
} from "@shared/services/event_tracking/entities";
import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
import type { CartOrderType, CartType } from "@shared/types/Cart";
import type { CartProduct } from "@shared/types/ProductLine";
import type { StockLevels } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";

const { trackClick, trackExternalCarrotAddToCart, trackEvent } = EventTracking;

type TargetObjectMap = {
  add: string;
  remove: string;
  set: string;
};

const buildTargetObjectMap = (
  targetObjectTypeBase: CartWidgetTargetObjectBase,
): TargetObjectMap => {
  return {
    add: `${targetObjectTypeBase}_cart_widget_add_button`,
    remove: `${targetObjectTypeBase}_cart_widget_remove_button`,
    set: `${targetObjectTypeBase}_cart_widget_input_field`,
  };
};

interface CartEvent {
  detail: {
    oldQuantity: number;
    newQuantity: number;
    quantityType: "case" | "unit";
    trackingAction: null | "add" | "remove" | "set";
    eventName: "cart_add" | "cart_remove";
    stockLevel: StockLevels;
  };
}

export const findProductForInventory = (cart: CartType, inventoryId: number): CartProduct => {
  if (!cart?.orders?.length) return;
  let product: CartProduct;

  for (const order of cart.orders) {
    product = order.products.find(orderProduct => orderProduct.inventory_id === inventoryId);
    if (product) return product;
  }
};

const findOrderForInventory = (cart: CartType, inventoryId: number): CartOrderType => {
  if (!cart?.orders?.length) return;

  return cart.orders.find(order =>
    order.products.some(product => product.inventory_id === inventoryId),
  );
};

const buildCartTrackingEntities = (
  cart: CartType,
  inventoryId: number,
  event: CartEvent,
): BaseEntityClass[] => {
  const product = findProductForInventory(cart, inventoryId);
  const order = findOrderForInventory(cart, inventoryId);

  return [
    new CartApiMetadataEntity({
      type: "distributor",
      id: order.distributor.id,
    }),
    new CartApiMetadataEntity({
      type: "order",
      id: order.id,
    }),
    new CartApiMetadataEntity({
      type: "order_group",
      id: cart.id,
    }),
    new CartProductEntity({
      productId: product.product_id,
      productLineId: product.product_line.id,
      unitQuantity:
        event.detail.quantityType === "unit"
          ? Math.abs(event.detail.newQuantity - event.detail.oldQuantity)
          : 0,
      caseQuantity:
        event.detail.quantityType === "case"
          ? Math.abs(event.detail.newQuantity - event.detail.oldQuantity)
          : 0,
      distributor_id: order.distributor_id,
      order_group_id: cart.id,
      order_id: order.id,
      inventory_id: inventoryId,
      stockLevel: event.detail.stockLevel,
    }),
  ];
};

export const sendClickTracking = (
  targetObjectTypeBase: CartWidgetTargetObjectBase,
  event: CartEvent,
  trackingEntities: BaseEntityClass[],
) => {
  const targetObjectMap = buildTargetObjectMap(targetObjectTypeBase);

  const { trackingAction } = event.detail;
  trackClick(targetObjectMap[trackingAction], trackingEntities, false);
};

export const sendCartTrackingEvents = (
  event: CartEvent,
  trackingEntities: BaseEntityClass[],
  inventoryId: number,
) => {
  const entities = buildCartTrackingEntities(get(CartStore), inventoryId, event).concat(
    trackingEntities,
  );
  const { eventName } = event.detail;
  trackEvent(eventName, entities);
  if (eventName === "cart_add" && entities.length) {
    trackExternalCarrotAddToCart(trackingEntities);
  }
};
